import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { QrCode } from "./QrCode";
import QRCode from "qrcode.react";
import api from "../utils/api";
import { formatIndianCurrency } from "../utils/Const";
import { Users, UserPlus, Copy, RotateCcw, X, CreditCard, Share2 } from "lucide-react";

const FUNNY_QUOTES = [
  "The gods of dining have spoken: [Name] is our generous savior tonight! 🌟",
  "May your wallet rest in peace, [Name]! Good karma is coming your way. 💸",
  "All hail [Name], the absolute legend of this table! 👑",
  "Congratulations [Name]! You won the grand prize: the check! 🥳",
  "Legend says [Name] always picks up the tab. Today, the legend continues! 🏆",
  "Your wallet's sacrifice is appreciated by all, [Name]! 💳",
  "Don't cry because it happened, smile because you paid, [Name]! 😂"
];

const BillView = () => {
  const { orderId } = useParams();
  const [bill, setBill] = useState(null);
  const billUrl = bill?.orderId ? `${window.location.origin}/#/bill/${bill.orderId}` : "";
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState(false);

  // Split-Bill States
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitMode, setSplitMode] = useState("equal"); // "equal" | "roulette"
  const [names, setNames] = useState(["You"]);
  const [newName, setNewName] = useState("");
  const [isSpinning, setIsSpinning] = useState(false);
  const [currentRollingIndex, setCurrentRollingIndex] = useState(0);
  const [winnerName, setWinnerName] = useState("");
  const [winnerQuote, setWinnerQuote] = useState("");
  const [toastMsg, setToastMsg] = useState("");

  const spinTimerRef = useRef(null);

  const fetchBill = useCallback(async () => {
    try {
      if (!orderId) return;

      const response = await api.get(`/public/bill/${orderId}`);
      setBill(response.data);
    } catch (error) {
      console.error("Error fetching bill:", error);
      setBill(null);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchBill();
    return () => {
      if (spinTimerRef.current) clearTimeout(spinTimerRef.current);
    };
  }, [fetchBill]);

  const handleUPIPayment = () => {
    const upiUrl = `upi://pay?pa=${bill.restaurantId?.upiId}&pn=${bill.restaurantId?.shop_type}&am=${bill.totalAmount}&cu=INR`;
    window.location.href = upiUrl;
  };

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(""), 3000);
  };

  // Add a participant name
  const addName = (e) => {
    e.preventDefault();
    const cleanName = newName.trim();
    if (!cleanName) return;
    if (names.includes(cleanName)) {
      showToast("Name already exists!");
      return;
    }
    setNames([...names, cleanName]);
    setNewName("");
  };

  // Remove a name
  const removeName = (indexToRemove) => {
    if (names.length <= 1) {
      showToast("Must have at least one participant!");
      return;
    }
    const filtered = names.filter((_, idx) => idx !== indexToRemove);
    setNames(filtered);
    if (winnerName) {
      setWinnerName("");
      setWinnerQuote("");
    }
  };

  // Gamified Rolling Roulette with Deceleration
  const startRouletteSpin = () => {
    if (names.length < 2) {
      showToast("Add at least 2 people to play!");
      return;
    }
    setIsSpinning(true);
    setWinnerName("");
    setWinnerQuote("");
    
    const finalWinnerIndex = Math.floor(Math.random() * names.length);
    let currentDelay = 40; // Starts fast
    let steps = 0;
    let index = currentRollingIndex;

    const runTick = () => {
      index = (index + 1) % names.length;
      setCurrentRollingIndex(index);
      steps += 1;

      // Gradually slow down
      if (steps > 20) {
        currentDelay += 35;
      } else {
        currentDelay += 8;
      }

      // Check if we hit final speed threshold and land on the winner
      if (currentDelay > 500 && index === finalWinnerIndex) {
        setIsSpinning(false);
        const winner = names[finalWinnerIndex];
        setWinnerName(winner);
        
        // Generate random quote
        const randomQuoteTemplate = FUNNY_QUOTES[Math.floor(Math.random() * FUNNY_QUOTES.length)];
        setWinnerQuote(randomQuoteTemplate.replace("[Name]", winner));
      } else {
        spinTimerRef.current = setTimeout(runTick, currentDelay);
      }
    };

    runTick();
  };

  // Clipboard Copier
  const copyToClipboard = (text, type = "split") => {
    navigator.clipboard.writeText(text);
    showToast(type === "split" ? "Split details copied!" : "Winner quote copied!");
  };

  // Share Bill Link
  const handleShareLink = () => {
    const billUrl = window.location.href;
    navigator.clipboard.writeText(billUrl);
    showToast("Bill link copied to clipboard!");
  };

  if (loading) {
    return <h1 className="mt-50 text-center h-1">Loading...</h1>;
  }

  if (!bill) {
    return <h1 className="mt-50 text-center h-1">Bill Not Found!!</h1>;
  }

  const discountPercent = Math.round((bill.discountPercentage ?? 0) * 100) / 100;
  const discountAmount = bill.discountAmount > 0
    ? bill.discountAmount
    : (bill.subtotal * discountPercent) / 100;
  const amountAfterDiscount = bill.taxableAmount > 0
    ? bill.taxableAmount
    : bill.subtotal - discountAmount;
  const gstPercent = bill.gstPercentage ?? 0;
  const gstAmount = bill.gstAmount > 0
    ? bill.gstAmount
    : (amountAfterDiscount * gstPercent) / 100;
  const shareAmount = bill.totalAmount / names.length;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 overflow-y-auto p-4">
      {/* Toast Alert */}
      {toastMsg && (
        <div className="fixed top-5 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-5 py-3 rounded-full text-sm font-bold shadow-lg z-50 transition-all duration-300">
          {toastMsg}
        </div>
      )}

      <div className="mt-20 mb-10 bg-white p-4 md:p-6 rounded-2xl shadow-xl w-full max-w-[460px] mx-auto flex flex-col gap-4">
        {/* Pending Bill Generation Notice Banner */}
        {bill.status === "PENDING_BILL_GENERATION" && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 flex flex-col gap-1 text-center shadow-inner">
            <span className="text-xs font-black uppercase text-amber-600 tracking-wider flex items-center justify-center gap-1.5 animate-pulse">
              ⏳ Order Active (Bill Pending)
            </span>
            <p className="text-[11px] text-gray-500 font-semibold leading-relaxed">
              Your items are being prepared. The restaurant hasn't generated the final invoice yet. You can view the items, current total, and split the bill below.
            </p>
            <div className="mt-1.5 inline-flex items-center justify-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-[10px] font-black uppercase tracking-wider w-fit mx-auto">
              Status: {bill.orderStatus || "PROCESSING"}
            </div>
          </div>
        )}

        {/* Bill Print Card */}
        <div id="bill-print-area" className="bill-slip bg-slate-50 p-4 rounded-xl border border-slate-100">
          <div className="w-full">
            <div className="bill">
              <h2 className="text-center text-2xl font-extrabold text-gray-900 tracking-tight mb-1">
                {bill.restaurantId?.shop_type}
              </h2>
              <div className="border-b-2 border-dotted border-gray-300 pb-3 mb-3">
                <p className="text-center text-xs text-gray-500 font-medium">
                  {bill.restaurantId?.address}
                </p>
                {(bill.restaurantId?.publicMobile || bill.restaurantId?.mobile) && (
                  <p className="text-center text-xs text-gray-500 font-medium">
                    📞 {bill.restaurantId?.publicMobile || bill.restaurantId?.mobile}
                  </p>
                )}
              </div>
              
              <div className="bill-details text-xs text-gray-600 flex flex-col gap-1 pb-3 mb-3 border-b-2 border-dotted border-gray-300">
                <p className="flex justify-between">
                  <span>Bill No:</span> <span className="font-bold text-gray-950">{bill?.billId}</span>
                </p>
                <p className="flex justify-between">
                  <span>Date:</span>
                  <span className="font-semibold">
                    {new Date(bill?.billDate).toLocaleDateString("en-GB")}{" "}
                    {new Date(bill?.billDate).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true })}
                  </span>
                </p>

                {(bill.customerName || bill.customerMobile) && (
                  <div className="mt-1 pt-1 border-t border-slate-200/60">
                    {bill.customerName && (
                      <p className="flex justify-between">
                        <span>Customer Name:</span> <span className="font-semibold">{bill?.customerName}</span>
                      </p>
                    )}
                    {bill.customerMobile && (
                      <p className="flex justify-between">
                        <span>Mobile:</span> <span className="font-semibold">{bill?.customerMobile}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>

              <h3 className="text-sm font-bold text-gray-800 mb-2">Order Summary :</h3>
              <div className="grid grid-cols-4 gap-2 text-xs font-black text-gray-400 uppercase tracking-wider border-b pb-1.5 mb-2">
                <span>Item</span>
                <span className="text-center">Rate</span>
                <span className="text-center">Qty</span>
                <span className="text-right">Amount</span>
              </div>

              <ul className="mb-3 max-h-[160px] overflow-y-auto text-xs flex flex-col gap-1.5 scrollbar-thin">
                {bill?.items?.map((item, idx) => (
                  <li key={item._id || `${item.itemId}-${idx}`} className="py-1 border-b border-gray-150/40 last:border-0">
                    <div className="grid grid-cols-4 gap-2 text-gray-700 font-medium">
                      <span className="col-span-1 break-words font-semibold">{item.name}</span>
                      <span className="text-center font-mono">₹{formatIndianCurrency(item.price)}</span>
                      <span className="text-center font-mono">{item.qty}</span>
                      <span className="text-right font-mono font-bold text-gray-950">
                        ₹{formatIndianCurrency(item.price * item.qty)}
                      </span>
                    </div>
                    {item.variant && (
                      <div className="text-[10px] text-indigo-600 font-bold pl-1 mt-0.5">
                        Size: {item.variant.name}
                      </div>
                    )}
                    {item.addOns && item.addOns.length > 0 && (
                      <div className="text-[10px] text-teal-600 font-semibold pl-1 mt-0.5">
                        + {item.addOns.map(a => a.name).join(", ")}
                      </div>
                    )}
                  </li>
                ))}
              </ul>

              <div className="flex flex-col gap-1 text-xs border-t pt-2.5">
                <p className="flex justify-between text-gray-600 font-medium">
                  <span>Sub Total :</span>
                  <span className="font-mono">₹{formatIndianCurrency(bill?.subtotal)}</span>
                </p>

                {discountAmount >= 0.01 && (
                  <p className="flex justify-between text-gray-600 font-medium">
                    <span>Discount ({discountPercent}%):</span>
                    <span className="font-mono text-rose-600">- ₹{formatIndianCurrency(discountAmount)}</span>
                  </p>
                )}

                {gstAmount >= 0.01 && (
                  <p className="flex justify-between text-gray-600 font-medium">
                    <span>GST ({bill.gstPercentage ?? bill.gst ?? 0}%):</span>
                    <span className="font-mono">+ ₹{formatIndianCurrency(gstAmount)}</span>
                  </p>
                )}

                {(bill?.platformFeeAmount >= 0.01 || bill?.platformFee > 0) && (
                  <p className="flex justify-between text-gray-600 font-medium">
                    <span>Processing Fee:</span>
                    <span className="font-mono">+ ₹{formatIndianCurrency(bill?.platformFeeAmount)}</span>
                  </p>
                )}

                {bill?.loyaltyDiscountAmount > 0 && (
                  <p className="flex justify-between font-semibold text-indigo-650">
                    <span>Loyalty Discount:</span>
                    <span className="font-mono">- ₹{formatIndianCurrency(bill?.loyaltyDiscountAmount)}</span>
                  </p>
                )}

                {bill?.walletAmountRedeemed > 0 && (
                  <p className="flex justify-between font-semibold text-emerald-700">
                    <span>Paid from Wallet :</span>
                    <span className="font-mono">- ₹{formatIndianCurrency(bill?.walletAmountRedeemed)}</span>
                  </p>
                )}

                {/* Round Off */}
                {bill?.roundOff !== undefined && bill.roundOff !== 0 && (
                  <p className="flex justify-between font-semibold text-gray-700">
                    <span>Round Off :</span>
                    <span className="font-mono">{bill.roundOff > 0 ? "+" : "-"} ₹{formatIndianCurrency(Math.abs(bill.roundOff))}</span>
                  </p>
                )}

                <div className="flex justify-between text-lg font-black mt-2 border-t-2 border-b-2 border-gray-900 border-dotted py-2 print:border-black print:text-xl">
                  <span>Grand Total</span>
                  <span className="font-mono">₹{formatIndianCurrency(bill?.totalAmount)}</span>
                </div>

                {/* Print QR Code */}
                {billUrl && (
                  <div className="hidden print:flex flex-col items-center justify-center mt-6">
                    <QRCode value={billUrl} size={90} renderAs="svg" />
                    <span className="text-[10px] mt-2 font-bold text-black uppercase tracking-wider">Scan for Digital Receipt</span>
                  </div>
                )}

                <div className="text-center mt-6 mb-2 text-xs font-bold print:text-black hidden print:block">
                  Thank You! Visit Again
                </div>

                {bill?.loyaltyPointsEarned > 0 && (
                  <p className="text-[11px] text-center text-indigo-600 font-extrabold mt-2 animate-bounce">
                    🎉 You earned {bill?.loyaltyPointsEarned} loyalty points!
                  </p>
                )}
              </div>
            </div>

            {/* Offline QR Payment Trigger */}
            <div className="my-3 text-center print:hidden">
              {bill.restaurantId?.upiId && bill.status !== "PAID" && (
                <div className="mt-4 print:hidden">
                  {!showQR ? (
                    <button
                      onClick={() => setShowQR(true)}
                      className="text-sm bg-indigo-50 text-indigo-600 font-semibold py-2 px-5 rounded-full border border-indigo-200 hover:bg-indigo-100 transition-colors shadow-sm"
                    >
                      View QR Code to Pay
                    </button>
                  ) : (
                    <QrCode upiDetails={bill.restaurantId?.upiId} totalAmount={bill?.totalAmount} />
                  )}
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-3">Or</p>
                  <button
                    className="w-full bg-slate-900 hover:bg-black text-white font-bold py-3 px-6 rounded-xl transition shadow-md hover:scale-[1.01]"
                    onClick={handleUPIPayment}
                  >
                    {bill.status === "PENDING_BILL_GENERATION" ? "Pre-Pay Order" : "Pay Now"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Gamified Split-Bill Trigger Button & Share Actions */}
        <div className="flex flex-col gap-2.5 print:hidden">
          {bill.status !== "PAID" && (
            <button
              onClick={() => setShowSplitModal(true)}
              className="w-full py-3.5 bg-gradient-to-r from-pink-500 via-purple-650 to-indigo-600 text-white font-extrabold rounded-xl shadow-lg hover:opacity-95 transition-all text-center flex items-center justify-center gap-2 text-sm uppercase tracking-wider animate-pulse hover:animate-none"
            >
              <Users className="w-5 h-5" />
              Split Bill & Play 🎮
            </button>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleShareLink}
              className="flex-1 py-3 bg-slate-900 hover:bg-black text-white font-bold text-xs rounded-xl flex items-center justify-center gap-1.5 transition shadow-md hover:scale-[1.01]"
            >
              <Share2 className="w-4 h-4" /> Copy Bill Link
            </button>

            <a
              href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`Hey! Here is our bill of ${bill.restaurantId?.shop_type || "the restaurant"}. Check details and pay/split here: ${window.location.href}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl flex items-center justify-center gap-1.5 transition shadow-md hover:scale-[1.01] text-center"
            >
              <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                <path d="M12.012 2c-5.506 0-9.988 4.482-9.988 9.988 0 1.761.459 3.476 1.33 4.986L2 22l5.204-1.365a9.923 9.923 0 004.803 1.233c5.506 0 9.993-4.482 9.993-9.988 0-2.662-1.036-5.163-2.92-7.05a9.897 9.897 0 00-7.098-2.83zm.005 1.78c2.193 0 4.256.855 5.807 2.408a8.167 8.167 0 012.4 5.8c0 4.524-3.682 8.207-8.207 8.207-1.444 0-2.859-.38-4.1-1.102l-.294-.174-3.05.8.816-2.973-.192-.306a8.169 8.169 0 01-1.252-4.352c0-4.524 3.682-8.207 8.207-8.207zm-2.002 3.208c-.225 0-.414.07-.565.22-.15.148-.48.472-.48 1.151s.494 1.332.564 1.428c.07.096.974 1.488 2.362 2.088.33.143.587.229.789.293.332.106.634.09.873.055.267-.04.821-.336.938-.661.117-.326.117-.605.082-.662-.035-.057-.13-.09-.276-.163s-.863-.426-.996-.475-.23-.07-.327.075c-.097.147-.378.476-.463.574-.085.099-.17.11-.318.037-.148-.073-.625-.23-1.192-.734-.44-.393-.738-.879-.824-1.027-.086-.148-.009-.228.065-.301.067-.066.148-.172.222-.258.075-.086.1-.148.15-.246.05-.098.025-.184-.012-.258-.038-.074-.327-.79-.448-1.082-.119-.286-.239-.247-.328-.252-.085-.005-.183-.005-.282-.005z"/>
              </svg>
              Share WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* Modern Gamified Split-Bill Modal */}
      {showSplitModal && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl w-full max-w-[450px] shadow-2xl overflow-hidden border border-gray-100 relative animate-scale-up">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-700 to-indigo-700 p-5 text-white flex justify-between items-center">
              <div>
                <h3 className="text-lg font-black tracking-tight flex items-center gap-2">
                  <Users className="w-5 h-5 text-pink-400" />
                  Gamified Bill Splitter
                </h3>
                <p className="text-xs text-indigo-150 font-medium">Split equally or play roulette to pick one payer!</p>
              </div>
              <button
                onClick={() => setShowSplitModal(false)}
                className="p-1.5 hover:bg-white/10 rounded-full transition text-white/80 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content Tabs */}
            <div className="flex border-b border-gray-150">
              <button
                onClick={() => setSplitMode("equal")}
                className={`flex-1 py-3.5 text-xs font-black uppercase tracking-wider border-b-2 transition-all ${
                  splitMode === "equal"
                    ? "border-indigo-650 text-indigo-650"
                    : "border-transparent text-gray-500 hover:text-gray-900"
                }`}
              >
                📊 Split Equally
              </button>
              <button
                onClick={() => setSplitMode("roulette")}
                className={`flex-1 py-3.5 text-xs font-black uppercase tracking-wider border-b-2 transition-all ${
                  splitMode === "roulette"
                    ? "border-indigo-650 text-indigo-650"
                    : "border-transparent text-gray-500 hover:text-gray-900"
                }`}
              >
                🎯 Payer Roulette
              </button>
            </div>

            {/* Shared Participant Name Form */}
            <div className="p-5 border-b border-gray-50">
              <form onSubmit={addName} className="flex gap-2 mb-3">
                <input
                  type="text"
                  placeholder="Add friend's name..."
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="flex-1 px-4 py-2 text-sm border border-gray-250 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center gap-1 font-bold text-xs"
                >
                  <UserPlus className="w-4 h-4" /> Add
                </button>
              </form>

              {/* Active Participants Badge List */}
              <div className="flex flex-wrap gap-1.5 max-h-[100px] overflow-y-auto">
                {names.map((name, idx) => (
                  <span
                    key={name + idx}
                    className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 text-indigo-750 rounded-full text-xs font-bold border border-indigo-100"
                  >
                    {name}
                    <button
                      type="button"
                      onClick={() => removeName(idx)}
                      className="text-indigo-400 hover:text-indigo-850 transition"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* TAB CONTENT: Split Equally */}
            {splitMode === "equal" && (
              <div className="p-5 flex flex-col gap-4">
                <div className="bg-indigo-50/50 border border-indigo-100/50 p-4 rounded-2xl text-center">
                  <span className="text-xs font-extrabold uppercase text-indigo-500 tracking-wider">Per Person Share</span>
                  <h4 className="text-3xl font-black text-indigo-950 mt-1">₹{formatIndianCurrency(shareAmount)}</h4>
                  <p className="text-[11px] text-gray-400 mt-1">
                    Equally divided between {names.length} participant{names.length > 1 ? "s" : ""}
                  </p>
                </div>

                {/* Split list breakdown */}
                <div className="max-h-[140px] overflow-y-auto flex flex-col gap-2">
                  {names.map((name, idx) => (
                    <div key={name + idx} className="flex justify-between items-center text-sm font-bold text-gray-700 bg-slate-50 px-3.5 py-2.5 rounded-xl border border-slate-100">
                      <span>{name}</span>
                      <span className="font-mono text-indigo-600">₹{formatIndianCurrency(shareAmount)}</span>
                    </div>
                  ))}
                </div>

                {/* Share Action */}
                <button
                  onClick={() =>
                    copyToClipboard(
                      `Hey! Our share of the bill at ${bill.restaurantId?.shop_type || "the restaurant"} is ₹${formatIndianCurrency(shareAmount)} each. UPI details: ${bill.restaurantId?.upiId || ""}`
                    )
                  }
                  className="w-full py-3 bg-slate-900 hover:bg-black text-white font-extrabold text-sm rounded-xl flex items-center justify-center gap-2 transition shadow-md"
                >
                  <Copy className="w-4 h-4" /> Copy Share Payment Details
                </button>
              </div>
            )}

            {/* TAB CONTENT: Payer Roulette */}
            {splitMode === "roulette" && (
              <div className="p-5 flex flex-col gap-5 text-center">
                {/* Visual Drum Slot Scroller */}
                <div className="relative border-4 border-slate-900 h-28 bg-slate-950 rounded-2xl flex items-center justify-center overflow-hidden shadow-inner">
                  {/* Neon Indicator Arrows */}
                  <div className="absolute left-2 text-pink-500 font-black text-xl animate-pulse">▶</div>
                  <div className="absolute right-2 text-pink-500 font-black text-xl animate-pulse">◀</div>

                  {/* Scroller Drum */}
                  <div className="text-center">
                    {isSpinning ? (
                      <span className="text-2xl font-black tracking-wider text-pink-400 uppercase select-none animate-pulse">
                        {names[currentRollingIndex]}
                      </span>
                    ) : winnerName ? (
                      <span className="text-3xl font-black text-emerald-400 uppercase select-none animate-bounce">
                        👑 {winnerName}
                      </span>
                    ) : (
                      <span className="text-sm font-semibold text-gray-500 select-none px-4">
                        Ready to pick a victim? Let's roll!
                      </span>
                    )}
                  </div>

                  {/* Glass Shading Overlay */}
                  <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/50 via-transparent to-black/50"></div>
                </div>

                {/* Winner Quote Box */}
                {winnerName && winnerQuote && (
                  <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl shadow-sm animate-fade-in flex flex-col gap-3">
                    <p className="text-sm font-bold text-gray-700 italic">"{winnerQuote}"</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => copyToClipboard(winnerQuote, "quote")}
                        className="flex-1 py-2 bg-amber-500 hover:bg-amber-600 text-white font-extrabold text-xs rounded-lg flex items-center justify-center gap-1.5 transition"
                      >
                        <Copy className="w-3.5 h-3.5" /> Copy Quote
                      </button>
                      {bill.restaurantId?.upiId && (
                        <button
                          onClick={handleUPIPayment}
                          className="flex-1 py-2 bg-indigo-650 hover:bg-indigo-700 text-white font-extrabold text-xs rounded-lg flex items-center justify-center gap-1.5 transition"
                        >
                          <CreditCard className="w-3.5 h-3.5" /> Pay Now
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Spin Button */}
                <button
                  onClick={startRouletteSpin}
                  disabled={isSpinning || names.length < 2}
                  className="w-full py-3 bg-gradient-to-r from-pink-500 to-purple-650 disabled:from-gray-300 disabled:to-gray-400 text-white font-extrabold text-sm rounded-xl flex items-center justify-center gap-2 transition shadow-md hover:scale-[1.01]"
                >
                  <RotateCcw className={`w-4 h-4 ${isSpinning ? "animate-spin" : ""}`} />
                  {isSpinning ? "Selecting Victim..." : "Spin the Wheel! 🎡"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BillView;
